// The gap between the partners, the monthly cash-flow view, and the expenses — job and
// office — that both sides read.
//
// Pure data — no imports, ever. errors.js reads he.leads.needContact at module scope,
// so a part reaching back for it would hand that read a half-built object. See he.js.
const settlements = {
  settlements: {
    // Neutral, matching the nav label, because the screen no longer answers one
    // question: it leads with a single month's collection and states the cumulative gap
    // below. Each card carries its own heading, and the old title ("מצב הפער בין
    // השותפים") would now name only the second of them.
    title: 'התחשבנות בין השותפים',
    // THIS MONTH'S COLLECTION — the top card, and a different question from the gap in
    // every respect: one month rather than all time, money that arrived rather than
    // money anyone is owed. NO STRING HERE MAY SAY "פער" OR "לטובת". Both belong to the
    // cumulative card below, and a month's spread is not a claim about entitlement —
    // it says whose business issued the invoices that happened to be paid this month.
    monthlyCollection: {
      title: 'כמה נגבה בחודש זה',
      prevMonth: 'לחודש הקודם',
      nextMonth: 'לחודש הבא',
      total: 'סה"כ נכנס החודש',
      difference: 'ההפרש בגבייה',
      // Not "לטובת". A statement about where the money landed, not about who is due it.
      higherAt: 'יותר אצל',
      level: 'מאוזן',
    },
    // The cumulative card's own heading, now that a monthly card sits above it. It says
    // "מצטבר" in as many words, because a figure under a month selector is assumed to
    // belong to that month unless it says otherwise.
    gapTitle: 'הפער המצטבר — מתחילת העבודה המשותפת',
    // THE SCREEN STATES A GAP, IT DOES NOT DEMAND A PAYMENT. The partners never transfer
    // money between themselves, so no string here may say "חוב", "לשלם" or "להעביר" —
    // the figure is a standing offset that nothing pays down, and the wording is the
    // only thing stopping the old framing from creeping back in.
    noGap: 'אין פער — השותפים במאזן מלא',
    gapFavours: 'הפער הנוכחי לטובת',
    gapAgainst: 'מול',
    // The five lines of the per-partner statement, in the order they are read. Together
    // they ARE the formula, with every intermediate on screen: what came in, less what
    // went out of pocket, is what a partner holds; against what their share entitles
    // them to; and the difference is the gap. Nothing here is a total of anything else
    // on the screen, so no line can be read as a restatement of another.
    statement: {
      collected: 'נגבה אצלי',
      paidFromPocket: 'שילמתי מהכיס',
      // Office overhead is in this figure. Said here rather than in a note, because it
      // is the one thing about the line a reader could otherwise get wrong.
      paidFromPocketHint: 'כולל הוצאות משרד',
      inHand: 'יש אצלי ביד',
      entitled: 'מגיע לי לפי החלק ברווח',
      gap: 'הפער',
    },
    credit: 'לזכות',
    debit: 'לחובה',
    settled: 'מאוזן',
    // The accrued figure, kept as ONE line beside the cash statement rather than as a
    // second table with a paragraph under it. It answers "what have I made", which the
    // cash gap never does — and without it the gap gets read as a partner's profit.
    earnedLine: 'רווח שנצבר, כולל חשבוניות שטרם נגבו',
    // The figure that is deliberately NOT in the balance. Shown here because this is
    // the screen where its absence is most confusing: a partner records a real cost
    // and sees the balance stay put.
    unpaidExpenses: 'הוצאות שטרם שולמו',
    // What an income unit is CALLED — shared by every list that names one: the collected
    // list here, the monthly cash-flow view, and the dashboard's top-units tile. One
    // block because three screens print the same two badges and the same fallback.
    unit: {
      retainer: 'ריטיינר',
      job: 'פרויקט',
      // A unit whose record was deleted after the money was collected. The row still
      // belongs in the list, so it is named rather than dropped.
      unnamed: 'יחידה שנמחקה',
    },
    // The list behind each partner's collected figure — one line per engagement, and
    // the only drill-down this screen has left. Grouped by engagement and not by
    // payment on purpose: the question is "what did I collect", and an answer that
    // scrolls is not an answer.
    collectedList: {
      // "מכל הזמן" is load-bearing now that a month selector sits at the top of the
      // screen: without it this list reads as the selected month's, which it is not.
      title: 'מה נגבה אצל כל שותף — מכל הזמן',
      total: 'סה"כ נגבה',
      empty: 'עדיין לא נגבה כאן כסף.',
    },
    // (A `breakdown` block lived here: the seven columns of the per-unit sources table —
    // share, cash profit, entitlement, contribution — plus its office, unallocated and
    // rounding-residual rows. The table was correct and unreadable, which is a worse
    // combination than either alone, and the screen now states the balance as five plain
    // figures per partner instead. The calculator behind it is untouched and still
    // asserted on every request that asks for it; only this screen stopped asking.)
    //
    // The monthly cash-flow view: what money ACTUALLY MOVED, month by month. A different
    // question from the gap above it — the gap is cumulative and has no monthly value —
    // so nothing here may be called "פער". Both sides are real cash dates: money in on
    // the day the payment arrived, money out on the day the expense was paid.
    monthly: {
      title: 'תנועת הכסף לפי חודשים',
      month: 'חודש',
      collected: 'נכנס — נגבה אצלי',
      paid: 'יצא — שילמתי מהכיס',
      net: 'נטו לחודש',
      total: 'סה"כ מצטבר',
      collectedShort: 'נכנס',
      paidShort: 'יצא',
      office: 'הוצאות משרד',
      unitsFor: 'תנועת הכסף בחודש',
      empty: 'עדיין לא זזה תנועת כסף עבור שותף זה.',
    },
    // (A `basisTitle` / `earned` / `cashOwed` / `earnedNote` set lived here, driving a
    // two-column table and the paragraph beneath it. The accrued-vs-cash distinction it
    // carried is worth keeping and is kept — as `earnedLine` above, one line under the
    // statement instead of a table plus four lines of prose.)
  },
};

export default settlements;
